import React, { createContext, useContext, ReactNode } from 'react';
import { useTimezoneCache } from '@/hooks/useTimezoneCache';

interface TimezoneContextType {
  timezones: string[];
  loading: boolean;
  error: string | null;
  clearCache: () => void;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

export const TimezoneProvider = ({ children }: { children: ReactNode }) => {
  const timezoneData = useTimezoneCache();

  return (
    <TimezoneContext.Provider value={timezoneData}>
      {children}
    </TimezoneContext.Provider>
  );
};

/**
 * Hook to access cached timezone data throughout the app.
 * 
 * This replaces direct database queries to pg_timezone_names,
 * providing instant access to timezone data with zero database load.
 * 
 * @example
 * const { timezones, loading } = useTimezones();
 * 
 * if (loading) return <Spinner />;
 * 
 * return (
 *   <select>
 *     {timezones.map(tz => (
 *       <option key={tz} value={tz}>{tz}</option>
 *     ))}
 *   </select>
 * );
 */
export const useTimezones = () => {
  const context = useContext(TimezoneContext);
  if (context === undefined) {
    throw new Error('useTimezones must be used within a TimezoneProvider');
  }
  return context;
};
